<template>
  <v-card class="mx-auto" max-width="460" rounded="lg" elevation="6">
    <v-toolbar dense tile flat color="grey lighten-3">
      <v-toolbar-title class="align-center d-flex text-subtitle-1">
        <v-icon class="mr-2" color="primary">mdi-shield-lock</v-icon>
        Set Up Two-Factor Authentication</v-toolbar-title
      >
    </v-toolbar>
    <v-divider />

    <v-img
      src="/assets/SDLOGO_STACKED.png"
      max-width="190"
      class="mx-auto mt-6 mb-2"
    />

    <v-row justify="center" class="mt-0">
      <v-col md="12">
        <v-alert
          :value="alert.show"
          :type="alert.type"
          :icon="alertIcon"
          text
          dense
          transition="fade-transition"
          class="mx-4 mt-2 mb-0 caption"
          >{{ alert.message }}</v-alert
        >

        <!-- Post-auth: continuous feedback until navigation swaps in the app. -->
        <v-card-text v-if="signingIn" class="text-center py-10">
          <v-progress-circular
            indeterminate
            color="primary"
            size="40"
            class="mb-3"
          />
          <div class="text-subtitle-1 grey--text text--darken-2">
            Signing you in…
          </div>
        </v-card-text>

        <v-card-text v-if="!showOtpInput && !signingIn" class="px-4 pt-2 pb-0">
          <div
            class="text-subtitle-2 text-center grey--text text--darken-2 mb-4"
          >
            Choose where to receive your verification code
          </div>
          <v-radio-group
            v-model="selectedContactMethod"
            class="mt-0"
            hide-details
          >
            <v-sheet
              v-for="method in contactMethods"
              :key="method.id"
              outlined
              rounded="lg"
              class="method-option d-flex align-center pa-3 mb-3"
              :class="{
                'method-option--selected': selectedContactMethod === method.id,
              }"
              @click="selectedContactMethod = method.id"
            >
              <v-avatar
                size="40"
                class="mr-3"
                :color="
                  selectedContactMethod === method.id
                    ? 'primary'
                    : 'grey lighten-3'
                "
              >
                <v-icon
                  :color="
                    selectedContactMethod === method.id
                      ? 'white'
                      : 'grey darken-1'
                  "
                >
                  {{ methodIcon(method.type) }}
                </v-icon>
              </v-avatar>
              <div class="flex-grow-1" style="min-width: 0">
                <div class="subtitle-2">
                  {{ contactTypeLabel(method.type) }}
                </div>
                <div class="caption grey--text text--darken-1 text-truncate">
                  {{ method.contact }}
                </div>
              </div>
              <v-radio :value="method.id" class="ma-0 pr-1" />
            </v-sheet>
          </v-radio-group>
        </v-card-text>
        <v-divider v-if="!showOtpInput && !signingIn" />
        <v-card-actions v-if="!showOtpInput && !signingIn" class="px-4 py-3">
          <v-btn :disabled="loading" @click="skipTwoFactor">
            Skip for 30 Days
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            depressed
            :loading="loading"
            :disabled="!selectedContactMethod"
            @click="initiateActivation"
          >
            Proceed
          </v-btn>
        </v-card-actions>

        <v-card-text v-if="showOtpInput && !signingIn" class="px-4 pt-2 pb-0">
          <div class="text-subtitle-2 text-center grey--text text--darken-2">
            Enter the 6-digit code sent to
          </div>
          <div class="text-center font-weight-medium mb-4">
            {{ selectedContactMethodValue?.contact }}
          </div>
          <v-otp-input
            v-model="otpCode"
            length="6"
            type="number"
            @finish="verifyOtp"
          />
          <div v-if="showResendOption" class="text-center mt-3">
            <span v-if="countdownActive" class="caption grey--text">
              Resend code in {{ formatTime(countdown) }}
            </span>
            <v-btn
              v-else
              text
              small
              color="primary"
              class="text-none"
              :disabled="resendDisabled"
              @click="resendOtp"
            >
              Resend code
            </v-btn>
          </div>
        </v-card-text>
        <v-divider v-if="showOtpInput && !signingIn" />
        <v-card-actions v-if="showOtpInput && !signingIn" class="px-4 py-3">
          <v-btn color="default" :disabled="loading" @click="skipTwoFactor">
            Skip for 30 Days
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            depressed
            :loading="loading"
            :disabled="!otpCode"
            @click="verifyOtp"
          >
            Verify
          </v-btn>
        </v-card-actions>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { setToken } from "../../apollo-client/apollo-token";
import { titleCase } from "@tod-ui/helpers/strings";
import { contactTypeLabel } from "../users/contactMethods";
import useUser from "./useUser";
import useAlerts from "@tod-ui/composables/useAlerts";
import {
  GET_CONTACT_METHODS,
  INITIATE_TWO_FACTOR_ACTIVATION,
  ACTIVATE_TWO_FACTOR_AND_LOGIN,
  SKIP_TWO_FACTOR_AND_LOGIN,
} from "./graphql";

const TFA_SETUP_TOKEN_KEY = "tfa_setup_token";

export default {
  name: "TwoFactorSetup",
  props: {
    token: {
      type: String,
      default: null,
    },
  },
  setup() {
    const { defaultPage, setCurrentUser } = useUser();
    return { defaultPage, setCurrentUser };
  },
  data() {
    const { localAlerts, addLocalAlert, clearAllLocalAlerts } = useAlerts();

    function showError(message) {
      addLocalAlert({ message, timeout: 3 });
    }
    return {
      contactMethods: [],
      selectedContactMethod: null,
      selectedContactMethodValue: null,
      showOtpInput: false,
      otpCode: "",
      loading: false,
      // True once auth succeeds: shows a "Signing you in…" state and keeps the
      // spinner up through the post-auth setup + route transition, so the
      // feedback is continuous until the dashboard takes over.
      signingIn: false,
      error: null,
      showResendOption: false,
      countdownActive: false,
      countdown: 30,
      countdownTimer: null,
      resendDisabled: false,
      effectiveToken: null,
      localAlerts,
      addLocalAlert,
      clearAllLocalAlerts,
      showError,
      alert: {
        show: false,
        type: null,
        message: null,
      },
    };
  },
  computed: {
    // Lighter outline icons for the tonal alert (the default filled circles are
    // too heavy against the soft background).
    alertIcon() {
      return (
        {
          success: "mdi-check-circle-outline",
          error: "mdi-alert-circle-outline",
          warning: "mdi-alert-outline",
          info: "mdi-information-outline",
        }[this.alert.type] || "mdi-information-outline"
      );
    },
  },
  created() {
    if (this.token) {
      localStorage.setItem(TFA_SETUP_TOKEN_KEY, this.token);
    }

    this.effectiveToken =
      this.token || localStorage.getItem(TFA_SETUP_TOKEN_KEY);

    if (!this.effectiveToken) {
      this.error = "Authentication required. Please log in again.";
      setTimeout(() => {
        this.$router.push({ name: "Login" }).catch((err) => {
          if (err.name !== "NavigationDuplicated") throw err;
        });
      }, 3000);
      return;
    }

    this.fetchContactMethods();
  },
  beforeDestroy() {
    this.clearCountdownTimer();
  },
  methods: {
    displayError(message) {
      this.error = message;
      const formattedText = titleCase(message);
      this.showError(formattedText);

      this.alert = {
        message: formattedText,
        type: "error",
        show: true,
      };
    },

    isExpiredSessionError(error) {
      const message = error?.graphQLErrors?.[0]?.message;
      return (
        message === "Failed to fetch login attempt" ||
        message === "session_not_found"
      );
    },

    handleExpiredSession() {
      this.displayError("Your session expired. Please sign in again");
      localStorage.removeItem(TFA_SETUP_TOKEN_KEY);
      setTimeout(() => {
        this.$router.push({ name: "Login" }).catch((err) => {
          if (err.name !== "NavigationDuplicated") throw err;
        });
      }, 3000);
    },

    async fetchContactMethods() {
      this.loading = true;
      try {
        const result = await this.$apollo.query({
          query: GET_CONTACT_METHODS,
          variables: {
            token: this.effectiveToken,
          },
          fetchPolicy: "network-only",
        });

        if (result.data?.userMContactMethods) {
          this.contactMethods = result.data.userMContactMethods;
        }
      } catch (error) {
        this.displayError("Failed to load contact methods");
        localStorage.removeItem(TFA_SETUP_TOKEN_KEY);
        setTimeout(() => {
          this.$router.push({ name: "Login" }).catch((err) => {
            if (err.name !== "NavigationDuplicated") throw err;
          });
        }, 3000);
      } finally {
        this.loading = false;
      }
    },
    async skipTwoFactor() {
      this.loading = true;
      this.error = null;
      this.clearAllAlerts();

      try {
        const response = await this.$apollo.mutate({
          mutation: SKIP_TWO_FACTOR_AND_LOGIN,
          variables: {
            token: this.effectiveToken,
          },
        });

        const result = response.data?.skipTwoFactorAndLogin;
        if (result?.token) {
          this.finishSignIn(result.token, result.user);
          return;
        }
        this.displayError("Failed to skip two-factor setup");
        this.loading = false;
      } catch (error) {
        this.loading = false;
        if (this.isExpiredSessionError(error)) {
          this.handleExpiredSession();
          return;
        }
        this.displayError("Failed to skip two-factor setup");
      }
    },
    async initiateActivation() {
      if (!this.selectedContactMethod) {
        this.error = "Please select a contact method";
        return;
      }

      this.loading = true;
      this.error = null;
      this.clearAllAlerts();
      try {
        await this.$apollo.mutate({
          mutation: INITIATE_TWO_FACTOR_ACTIVATION,
          variables: {
            token: this.effectiveToken,
            contactMethodId: this.selectedContactMethod,
          },
        });

        this.showOtpInput = true;
        this.selectedContactMethodValue = this.contactMethods.find(
          (method) => method.id === this.selectedContactMethod
        );
        this.showResendOption = true;

        this.alert = {
          message: "Verification code sent.",
          type: "success",
          show: true,
        };
        this.startCountdown();
      } catch (error) {
        if (this.isExpiredSessionError(error)) {
          this.handleExpiredSession();
          return;
        }
        this.displayError(error.graphQLErrors[0].message);
        if (error.graphQLErrors[0].message == "max_otp_retries_reached") {
          localStorage.removeItem(TFA_SETUP_TOKEN_KEY);
          setTimeout(() => {
            this.$router.push({ name: "Login" }).catch((err) => {
              if (err.name !== "NavigationDuplicated") throw err;
            });
          }, 3000);
        }
      } finally {
        setTimeout(this.clearAllAlerts, 5000);
        this.loading = false;
      }
    },
    async verifyOtp() {
      this.loading = true;
      this.error = null;
      this.clearAllAlerts();
      try {
        const response = await this.$apollo.mutate({
          mutation: ACTIVATE_TWO_FACTOR_AND_LOGIN,
          variables: {
            token: this.effectiveToken,
            otp: this.otpCode,
          },
        });

        const result = response.data?.activateTwoFactorAndLogin;
        if (result?.token) {
          this.finishSignIn(result.token, result.user);
          return;
        }
        this.displayError("Verification failed");
        this.showResendOption = true;
        this.otpCode = "";
        this.loading = false;
      } catch (error) {
        this.loading = false;
        if (this.isExpiredSessionError(error)) {
          this.handleExpiredSession();
          return;
        }
        this.displayError("Invalid verification code");
        this.showResendOption = true;
        this.otpCode = "";
      }
    },

    // Store the auth token + user, then hard-navigate to the landing page.
    //
    // A full page load (rather than a soft router.replace) is deliberate: this
    // page is `withoutAuth`, and a soft replace to `$lastRoute || defaultPage`
    // could resolve to a no-op here — leaving the app shell rendered but the
    // route stuck on /two-factor-setup with a perpetual "Signing you in…". A
    // reload guarantees we leave this page and boots the app cleanly
    // authenticated (App.vue restores the user + facility from what we persist
    // here). Keeps the spinner up until the browser unloads.
    async finishSignIn(token, user) {
      localStorage.removeItem(TFA_SETUP_TOKEN_KEY);
      setToken(token);
      if (user) this.setCurrentUser(user);
      this.clearAllAlerts();
      this.signingIn = true;

      // `setCurrentUser` above already derived the navigable scope from the user's
      // roles, so `defaultPage` resolves to the role-correct landing here (single
      // facility → FacilityDashboard, multi-scope → ManagementSummary) with no
      // scope fetch.
      const { href } = this.$router.resolve(this.defaultPage);
      window.location.assign(href || "/");
    },
    async resendOtp() {
      this.clearAllAlerts();
      this.loading = true;
      this.error = null;
      try {
        await this.$apollo.mutate({
          mutation: INITIATE_TWO_FACTOR_ACTIVATION,
          variables: {
            token: this.effectiveToken,
            contactMethodId: this.selectedContactMethod,
          },
        });

        this.startCountdown();
        this.otpCode = "";

        this.alert = {
          message: "Verification code sent.",
          type: "success",
          show: true,
        };
        setTimeout(this.clearAllAlerts, 5000);
      } catch (error) {
        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
          const errorMessage = error.graphQLErrors[0].message;
          this.displayError(errorMessage);
          if (
            errorMessage === "Maximum OTP resend attempts reached" ||
            errorMessage === "Failed to fetch login attempt"
          ) {
            localStorage.removeItem(TFA_SETUP_TOKEN_KEY);
            setTimeout(() => {
              this.$router.push({ name: "Login" }).catch((err) => {
                if (err.name !== "NavigationDuplicated") throw err;
              });
            }, 3000);
          }
        } else {
          this.displayError("Failed to resend verification code");
        }
      } finally {
        this.loading = false;
      }
    },
    startCountdown() {
      this.countdown = 30;
      this.countdownActive = true;
      this.resendDisabled = true;

      this.clearCountdownTimer();

      this.countdownTimer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          this.countdownActive = false;
          this.resendDisabled = false;
          this.clearCountdownTimer();
        }
      }, 1000);
    },
    clearCountdownTimer() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    },
    clearAllAlerts() {
      this.clearAlert();
      this.clearAllLocalAlerts();
    },
    methodIcon(type) {
      if (type === "email") return "mdi-email-outline";
      if (type === "mobile") return "mdi-cellphone";
      return "mdi-phone-outline";
    },
    clearAlert() {
      this.alert = {
        show: false,
        type: null,
        message: null,
      };
    },
    onAlertDismiss() {
      this.clearAlert();
    },
    titleCase,
    contactTypeLabel,
  },
};
</script>

<style scoped>
/* Selectable contact-method cards. */
.method-option {
  cursor: pointer;
  border-color: rgba(0, 0, 0, 0.16) !important;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.method-option:hover {
  border-color: rgba(0, 0, 0, 0.32) !important;
  background-color: rgba(0, 0, 0, 0.02);
}

.method-option--selected {
  border-color: var(--v-primary-base) !important;
  background-color: rgba(var(--v-primary-base-rgb, 45, 46, 117), 0.06);
}
</style>
